import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils & misc
import cn from '../../../utils/cn';
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';

// Components
import Picto from '../../atoms/Picto/Picto';

// Style
import style from './NavigationModeSwitch.module.css';

/**
 * @typedef {object} NavigationModeOption
 * @property {string} id - Option id (mode value or action id)
 * @property {string} label - Accessible label (also displayed when active and `showLabel`)
 * @property {import('../../atoms/Picto/Picto').AvailableIconName | string} icon - Picto name or FontAwesome class
 * @property {boolean} [showLabel=false] - Display the label next to the icon when active
 */

/**
 * @param {string} icon
 * @returns {React.ReactNode}
 */
const renderIcon = (icon) => {
  const iconDefinition = parseFontAwesomeClass(icon);
  if (iconDefinition) return <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} />;
  return <Picto icon={/** @type {import('../../atoms/Picto/Picto').AvailableIconName} */ (icon)} />;
};

/**
 * NavigationModeSwitch component - Row of pill buttons switching the sidebar content.
 * Collapsed menu: only the active option is displayed.
 * @param {object} params
 * @param {NavigationModeOption[]} params.options - Buttons, in display order
 * @param {string} params.activeId - Id of the active option
 * @param {(id: string) => void} params.onSelect - Click handler
 * @param {boolean} [params.isMenuOpen=false] - Whether the menu is open
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} NavigationModeSwitch component
 */
function NavigationModeSwitch({
  options,
  activeId,
  onSelect,
  isMenuOpen = false,
  className,
}) {
  return (
    <div className={cn([style['mode-switch'], isMenuOpen && style['menu-open'], className])}>
      {options.map((option) => {
        const isActive = option.id === activeId;
        return (
          <button
            key={option.id}
            type="button"
            className={cn([
              style.button,
              isActive && style.active,
              isActive && option.showLabel && style['with-label'],
            ])}
            onClick={() => onSelect(option.id)}
            aria-pressed={isActive}
            aria-label={option.label}
            title={option.label}
            tabIndex={isMenuOpen || isActive ? 0 : -1}
          >
            <span className={style.icon}>{renderIcon(option.icon)}</span>
            {isActive && option.showLabel ? <span className={style.label}>{option.label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export default NavigationModeSwitch;
